import { NavigateFunction } from 'react-router-dom';
import { ApolloCache, useMutation } from '@apollo/client';
import { CREATE_PAYMENT_SESSION } from '~/services/payments';
import { apolloClient } from '~/services';
import { MessagingProducts } from '~/types';
import { CREATE_CHANNEL_INTENT } from '~/services/channel';

/**
 * Handles Instagram connection flow
 * @param agentId - The ID of the agent to connect
 */
export const handleConnectInstagram = async (agentId: string | undefined) => {
  if (!agentId) {
    console.error('Agent ID is required to connect Instagram');
    return;
  }

  sessionStorage.setItem('instagram_agent_id', `${agentId}`);
  const instagramLink = `https://www.instagram.com/oauth/authorize?client_id=${1447570836623155}&redirect_uri=${window.location.origin}/instagram/success&response_type=code&scope=instagram_business_basic,instagram_business_manage_messages,instagram_business_manage_comments,instagram_business_content_publish&state=${agentId}`;

  const { data } = await apolloClient.mutate({
    mutation: CREATE_CHANNEL_INTENT,
    variables: {
      data: {
        agent_id: agentId,
        messaging_product: MessagingProducts.INSTAGRAM,
      },
    },
  });

  window.location.href = instagramLink;
};

/**
 * Handles WhatsApp connection flow
 * @param agentId - The ID of the agent to connect
 * @param registerToken - The mutation function to register the token
 * @param navigate - The navigate function from react-router
 * @param refetch - Function to refetch data after successful connection
 */
export const handleConnectWhatsapp = async (
  agentId: string | undefined,
  registerToken: any,
  navigate: NavigateFunction,
  refetch: () => void,
) => {
  if (!agentId) {
    console.error('Agent ID is required to connect WhatsApp');
    return;
  }

  const { data } = await apolloClient.mutate({
    mutation: CREATE_CHANNEL_INTENT,
    variables: {
      data: {
        agent_id: agentId,
        messaging_product: MessagingProducts.WHATSAPP,
      },
    },
  });

  // Function to process the code received from Facebook
  const receivedCode = async (code: string) => {
    await registerToken({
      variables: {
        data: {
          code,
          agent_id: agentId,
        },
      },
      onCompleted: (res: any) => {
        if (res.register_meta_account) {
          refetch();
          // Check if we're already in the integrations page
          const currentPath = window.location.pathname;
          if (currentPath.includes('/integrations')) {
            // If in integrations page, navigate to wsp_success
            navigate(`wsp_success`);
          } else {
            // If in another page (like dashboard), redirect to the integrations page
            const baseAgentPath = currentPath.split('/').slice(0, 3).join('/');
            navigate(`${baseAgentPath}/integrations/wsp_success`);
          }
        }
      },
    });
  };

  // @ts-ignore - FB is injected globally
  FB.login(
    function (response: any) {
      console.log('Facebook login response:', response);
      if (response.authResponse && response.authResponse.code) {
        const code = response.authResponse.code;
        receivedCode(code);
      } else {
        console.log('User cancelled login or did not fully authorize.');
      }
    },
    {
      config_id: import.meta.env.VITE_FB_CONFIG_ID,
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        setup: {
          business: {
            id: agentId,
          },
        },
        userID: agentId, // Add userID parameter to get it back in the response
        featureType: 'whatsapp_business_app_onboarding', // set to 'whatsapp_business_app_onboarding'
        sessionInfoVersion: '3',
      },
    },
  );
};

/**
 * Handles Google Drive connection flow
 * @param agentId - The ID of the agent to connect
 * @param getDriveIntegrationUrl - Function to get the Drive integration URL
 */
export const handleConnectGoogleDrive = (
  agentId: string | undefined,
  getDriveIntegrationUrl: any,
) => {
  if (!agentId) {
    console.error('Agent ID is required to connect Google Drive');
    return;
  }
  localStorage.setItem('google_drive_agent_id', `${agentId}`);
  console.log('Attempting to connect to Google Drive...');
  getDriveIntegrationUrl({
    variables: {
      data: {
        agent_id: agentId,
      },
    },
  });
};

/**
 * Handles Instagram disconnection flow
 * @param agentId - The ID of the agent
 * @param refetch - Function to refetch data after disconnection
 * @param confirmMessage - Message to show in the confirmation dialog
 * @returns boolean - Whether the disconnection was confirmed by the user
 */
export const handleDisconnectInstagram = (
  agentId: string | undefined,
  refetch: () => void,
  confirmMessage: string,
): boolean => {
  if (!agentId) {
    console.error('Agent ID is required to disconnect Instagram');
    return false;
  }

  console.log(`Disconnecting Instagram for agent ${agentId}...`);

  // Ask for confirmation
  if (window.confirm(confirmMessage)) {
    // TODO: Call disconnect API or mutation
    // This is where you would implement the actual disconnect logic
    alert(
      `Instagram disconnected. Please implement the actual disconnect logic.`,
    );
    refetch(); // Refresh the integrations list
    return true;
  }

  return false;
};

/**
 * Handles generic service disconnection flow
 * @param agentId - The ID of the agent
 * @param serviceName - Name of the service to disconnect
 * @returns void
 */
export const handleGenericDisconnect = (
  agentId: string | undefined,
  serviceName: string,
): void => {
  if (!agentId) {
    console.error(`Agent ID is required to disconnect ${serviceName}`);
    return;
  }

  console.log(`Disconnecting ${serviceName} for agent ${agentId}...`);
  alert(`Disconnect functionality for ${serviceName} is not yet implemented.`);
};
